import {CanActivateFn} from '@angular/router';
import {inject} from '@angular/core';
import {ModuleConfigurationService} from '../services/module-configuration.service';
import {RouterService} from '../services/router.service';
import {ToasterService} from '../services/toaster.service';
import {GenericModelType} from '../types/generated/genericModelType';
import {NotificationActionType} from '../types/generated/notificationActionType';
import {LanguageService} from '../services/language.service';
import * as Sentry from '@sentry/angular';
import {catchError, from, map, of, switchMap, tap} from 'rxjs';
import {ApiService} from '../services/api.service';
import {TokenService} from '../services/token.service';
import {CapabilitiesService} from '../services/capabilities.service';
import {ExternalFeedbackSessionService} from '../services/external-feedback-session.service';

export const routeResolverGuard: CanActivateFn = (route, state) => {
  const moduleConfigurationService = inject(ModuleConfigurationService);
  const routerService = inject(RouterService);
  const toasterService = inject(ToasterService);
  const languageService = inject(LanguageService);
  const apiService = inject(ApiService);
  const tokenService = inject(TokenService);
  const capabilitiesService = inject(CapabilitiesService);
  const externalFeedbackSession = inject(ExternalFeedbackSessionService);

  const queryParam = (key: string) => route.queryParamMap.get(key) ?? route.queryParamMap.get(`amp;${key}`);
  const type = queryParam('type') as GenericModelType | undefined;
  const id = queryParam('id');
  const parentId = queryParam('parent_id');
  const action = queryParam('action') as NotificationActionType | null;
  const token = queryParam('token')?.trim() ?? '';
  const capability = queryParam('capability')?.trim() ?? '';
  const customerKey = queryParam('customerKey')?.trim() ?? '';

  if (type === GenericModelType.FEEDBACK_FORM) {
    if (id?.trim() && token !== '') {
      const publicFeedbackUrl = new URL(`/public-feedback/forms/${id.trim()}`, window.location.origin);
      publicFeedbackUrl.searchParams.set('token', token);
      if (customerKey !== '') {
        publicFeedbackUrl.searchParams.set('customerKey', customerKey);
      }

      routerService.navigateTo(`${publicFeedbackUrl.pathname}${publicFeedbackUrl.search}`);
      return false;
    }

    if (action === null) {
      return languageService.init().then(() => {
        toasterService.warning(
          languageService.getTranslationByKey('ANERROROCCURED'),
          languageService.getTranslationByKey('ROUTERESOLVINGFAILED')
        );

        routerService.navigateTo('/auth/customer');
        return false;
      });
    }
  }

  if (type === GenericModelType.FEEDBACK_INSTANCE) {
    if (id?.trim() && capability !== '') {
      return from(languageService.init()).pipe(
        switchMap(() => apiService.loginExternalUser(capability)),
        switchMap(({token}) => {
          const normalizedToken = token.trim();
          if (normalizedToken === '') {
            throw new Error('External login returned an empty token');
          }

          tokenService.setToken(normalizedToken, true);
          externalFeedbackSession.set(id.trim(), normalizedToken);
          return from(languageService.init()).pipe(
            tap(() => routerService.navigateTo(`/external-feedback/attempt/${encodeURIComponent(id.trim())}`))
          );
        }),
        map(() => false),
        catchError(() => {
          externalFeedbackSession.clear(id.trim());
          tokenService.clearOneTimeToken();
          routerService.navigateTo('/external-feedback/invalid-invite');
          return of(false);
        })
      );
    }

    if (action === null) {
      routerService.navigateTo('/external-feedback/invalid-invite');
      return false;
    }
  }

  const resolveRoute = () => {
    const resolvedRoute = moduleConfigurationService.getRouteForGenericModelResource(type, id, parentId, action);

    if (resolvedRoute) {
      routerService.navigateTo(resolvedRoute);
      return false;
    }

    return languageService.init().then(() => {
      toasterService.warning(
        languageService.getTranslationByKey('ANERROROCCURED'),
        languageService.getTranslationByKey('ROUTERESOLVINGFAILED')
      );

      Sentry.withScope((scope) => {
        scope.setLevel('info');
        scope.setTag('route.guard', 'route-resolver');
        scope.setExtra('requestedUrl', state.url);
        Sentry.captureMessage('Route resolving failed in routeResolverGuard');
      });

      routerService.navigateTo('/home');
      return false;
    });
  };

  if ((type === GenericModelType.FEEDBACK_FORM || type === GenericModelType.FEEDBACK_INSTANCE ||
    type === GenericModelType.FEEDBACK_ATTEMPT) && tokenService.hasToken() &&
    !capabilitiesService.capabilitiesLoaded()) {
    return capabilitiesService.fetchUserCapabilities().pipe(
      catchError(() => of(null)),
      switchMap((capabilities) => {
        if (capabilities !== null) capabilitiesService.applyCapabilities(capabilities);
        return from(Promise.resolve(resolveRoute()));
      })
    );
  }

  return resolveRoute();
};
